import React, { useMemo } from 'react';
import classnames from 'classnames';

import {
  LINE_ITEM_TYPES,
  PAYMENT_REQUEST_CATEGORIES
} from 'dpl/shared/constants/breeder_app';

import { isFormattedAmountZero } from './util';
import StripeChargeModalPaymentSummaryLineItemV2 from './StripeChargeModalPaymentSummaryLineItemV2';
import StripeChargeModalPaymentProcessingLineItem from './StripeChargeModalPaymentProcessingLineItem';
import StripeChargeModalPlatformFeeLineItem from './StripeChargeModalPlatformFeeLineItem';
import StripeChargeModalDepositLineItem from './StripeChargeModalDepositLineItem';
import StripeChargeModalTaxLineItem from './StripeChargeModalTaxLineItem';
import { PAYMENT_TIER_TYPE } from './constants';
import StripeChargeModalFeeBundleLineItem from './StripeChargeModalFeeBundleLineItem';

const FEE_TYPES = [LINE_ITEM_TYPES.FEE, LINE_ITEM_TYPES.PROCESSING_FEE];

type PaymentTierType =
  (typeof PAYMENT_TIER_TYPE)[keyof typeof PAYMENT_TIER_TYPE];
type LineItemType = (typeof LINE_ITEM_TYPES)[keyof typeof LINE_ITEM_TYPES];
type PaymentRequestCategory =
  (typeof PAYMENT_REQUEST_CATEGORIES)[keyof typeof PAYMENT_REQUEST_CATEGORIES];

interface LineItem {
  description: string;
  amount_in_dollars: string;
  type: LineItemType;
  item_type?: string;
  discount_price_in_dollars?: string;
  long_description?: string;
  tooltip_description?: string;
  tooltipDescription?: string;
  amount_in_dollars_after_discount?: string;
  discountedPriceInDollars?: string;
}

interface TaxBreakdownItem {
  heading?: string;
  description?: string;
}

interface FeeBundle {
  amount: string;
  description: string;
  long_description: string;
  tooltip_description: string;
  item_types: string[];
  processing_savings?: string | null;
}

interface StripeChargeModalPaymentSummaryV4Props {
  amountSubtotal: string | number;
  lineItems: LineItem[];
  paymentTier: PaymentTierType;
  category: PaymentRequestCategory;
  className?: string | null;
  onChangeSelection?: ((tier: PaymentTierType) => void) | null;
  paymentMethod?: string | null;
  disableInteractions?: boolean;
  taxAmount?: string | null;
  taxBreakdown?: TaxBreakdownItem[] | null;
  feeBundle?: FeeBundle | null;
  depositRefundPolicy?: string | null;
  depositSecures?: string | null;
  finalPaymentTiming?: string | null;
  // a dog payment's tax can't be worked out until there's a pickup location
  isTaxPending?: boolean;
}

// The total sits in the "Total due" header around this list (SummaryV3), so
// the list itself has no total.
export default function StripeChargeModalPaymentSummaryV4({
  amountSubtotal,
  className = null,
  lineItems,
  paymentTier,
  category,
  onChangeSelection = null,
  paymentMethod = null,
  disableInteractions = false,
  taxAmount = null,
  taxBreakdown = null,
  feeBundle = null,
  depositRefundPolicy = null,
  depositSecures = null,
  finalPaymentTiming = null,
  isTaxPending = false
}: StripeChargeModalPaymentSummaryV4Props) {
  const items = useMemo(() => {
    if (!feeBundle) return lineItems;
    // Exclude line items for individual fees (already included in the fee bundle)
    return lineItems.filter(item => !FEE_TYPES.includes(item.type));
  }, [feeBundle, lineItems]);

  function renderLineItem(lineItem: LineItem): React.ReactNode {
    const itemType = lineItem.type;
    const props = {
      amountInDollars: lineItem.amount_in_dollars,
      description: lineItem.description,
      disableInteractions,
      discountedPriceInDollars:
        lineItem.discountedPriceInDollars ||
        lineItem.amount_in_dollars_after_discount,
      itemType,
      longDescription: lineItem.long_description,
      tooltipDescription:
        lineItem.tooltipDescription || lineItem.tooltip_description,
      compact: true
    };

    if (
      category === PAYMENT_REQUEST_CATEGORIES.DEPOSIT &&
      lineItem.item_type === 'deposit'
    ) {
      return (
        <StripeChargeModalDepositLineItem
          {...props}
          // the refund policy reads as a badge beside the label
          longDescription={null}
          badge={lineItem.long_description}
          paymentMethod={paymentMethod}
          depositRefundPolicy={depositRefundPolicy}
          depositSecures={depositSecures}
          finalPaymentTiming={finalPaymentTiming}
        />
      );
    }

    if (itemType === LINE_ITEM_TYPES.PROCESSING_FEE) {
      return (
        <StripeChargeModalPaymentProcessingLineItem
          {...props}
          paymentMethod={paymentMethod}
        />
      );
    }

    if (itemType === LINE_ITEM_TYPES.FEE) {
      return (
        <StripeChargeModalPlatformFeeLineItem
          {...props}
          paymentTier={paymentTier}
          subtotalInDollars={amountSubtotal}
          dogPayment={category === PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT}
          paymentMethod={paymentMethod}
          onChangeSelection={tier => onChangeSelection?.(tier)}
        />
      );
    }

    return <StripeChargeModalPaymentSummaryLineItemV2 {...props} />;
  }

  return (
    <div className={classnames('StripeChargeModalPaymentSummaryV4', className)}>
      <ul className="flex flex-column gap-12 pt3">
        {items.map((item, idx) => (
          <li key={idx}>{renderLineItem(item)}</li>
        ))}
        {feeBundle?.amount && (
          <li>
            <StripeChargeModalFeeBundleLineItem
              feeBundle={feeBundle}
              paymentTier={paymentTier}
              subtotalInDollars={amountSubtotal}
              dogPayment={category === PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT}
              paymentMethod={paymentMethod}
              onTierChange={tier => onChangeSelection?.(tier)}
              disableInteractions={disableInteractions}
              compact
            />
          </li>
        )}
        {paymentTier !== PAYMENT_TIER_TYPE.PROTECTED && (
          <li>
            <StripeChargeModalPaymentSummaryLineItemV2
              amountInDollars="0"
              description="No Protection & Support"
              itemType="no-protection-and-support"
              compact
              longDescription={
                <>
                  This payment isn&apos;t protected through Good Dog or eligible
                  for support.{' '}
                  <button
                    type="button"
                    className="stone-700 underline"
                    onClick={() =>
                      onChangeSelection?.(PAYMENT_TIER_TYPE.PROTECTED)
                    }
                  >
                    Add Payment Protection
                  </button>
                </>
              }
            />
          </li>
        )}
        {Boolean(taxAmount) && !isFormattedAmountZero(taxAmount) && (
          <li>
            <StripeChargeModalTaxLineItem
              amountInDollars={taxAmount}
              description="Taxes"
              itemType={LINE_ITEM_TYPES.TAX}
              taxBreakdown={taxBreakdown}
              compact
            />
          </li>
        )}
        {isTaxPending && (
          <li>
            <StripeChargeModalTaxLineItem
              amountInDollars="0"
              amountText="TBD"
              description="Taxes"
              itemType={LINE_ITEM_TYPES.TAX}
              compact
            />
          </li>
        )}
      </ul>
    </div>
  );
}
